import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageAdmins = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ email: '', role: 'admin' });

  useEffect(() => {
    checkAccessAndFetchAdmins();
  }, []);

  const checkAccessAndFetchAdmins = async () => {
    try {
      const adminCheck = await axios.get(`${API}/admin/check`, { withCredentials: true });
      
      if (!adminCheck.data.is_admin || adminCheck.data.role !== 'super_admin') {
        navigate('/admin');
        return;
      }

      const response = await axios.get(`${API}/admin/admins`, { withCredentials: true });
      setAdmins(response.data);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/admins`, formData, { withCredentials: true });
      setIsDialogOpen(false);
      setFormData({ email: '', role: 'admin' });
      checkAccessAndFetchAdmins();
    } catch (error) {
      console.error('Failed to add admin:', error);
      alert(error.response?.data?.detail || 'Failed to add admin');
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm('Are you sure you want to remove this admin?')) return;
    
    try {
      await axios.delete(`${API}/admin/admins/${adminId}`, { withCredentials: true });
      checkAccessAndFetchAdmins();
    } catch (error) {
      console.error('Failed to delete admin:', error);
      alert(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mentality-300"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-mentality-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/admin" className="flex items-center space-x-3">
              <img 
                src="/mentality-logo.jpg" 
                alt="Mentality Logo" 
                className="w-10 h-10 md:w-12 md:h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-mentality-300 to-mentality-600 bg-clip-text text-transparent">
                  Manage Admins
                </h1>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
            </Link>
            <div className="flex items-center space-x-2 md:space-x-4">
              <Link to="/admin">
                <Button variant="ghost" size="sm">
                  <span className="hidden md:inline">Dashboard</span>
                  <span className="md:hidden">📊</span>
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={() => logout().then(() => navigate('/'))}
                className="border-mentality-200 hover:bg-mentality-50"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Admin Users</h2>
            <p className="text-gray-600">Manage admin access to the platform</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-purple-500 to-indigo-500" size="lg">
                + Add Admin
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Admin</DialogTitle>
                <DialogDescription>
                  Grant admin access to a user by their email address
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@gsumail.gram.edu"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role *</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Admins can manage sessions. Super Admins can also manage other admins.
                  </p>
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-purple-500 hover:bg-purple-600">
                    Add Admin
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Admins List */}
        <Card className="border-2 border-purple-200 shadow-lg">
          <CardHeader>
            <CardTitle>Current Admins ({admins.length})</CardTitle>
            <CardDescription>
              Users with administrative access to the platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            {admins.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No admins found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {admins.map((admin, index) => (
                  <div key={admin.id}>
                    <div className="flex items-center justify-between py-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="font-medium text-gray-900">{admin.email}</p>
                          <Badge className={
                            admin.role === 'super_admin' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-blue-100 text-blue-700'
                          }>
                            {admin.role === 'super_admin' ? '👑 Super Admin' : 'Admin'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">
                          Added {new Date(admin.created_at).toLocaleDateString()}
                          {admin.added_by && ` by ${admin.added_by}`}
                        </p>
                      </div>
                      {admin.email !== user.email && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(admin.id)}
                        >
                          Remove
                        </Button>
                      )}
                      {admin.email === user.email && (
                        <Badge variant="outline">You</Badge>
                      )}
                    </div>
                    {index < admins.length - 1 && <Separator />}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ManageAdmins;
